import React, { useEffect, useState } from 'react';
import { Loader, Message } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { useEmployeeDeleteImpact, usePermanentlyDeleteEmployee } from '../../hooks/useQueries';
import { showToast } from '../../utils/toast';

const CONFIRM_WORD = 'DELETE';

// Table names come straight from the database catalog. Strip the schema and turn
// the underscores into spaces so the dialog reads as English rather than DDL.
const prettyTable = (table) => String(table).split('.').pop().replace(/_/g, ' ');

export default function PermanentDeleteModal({ employee: employeeProp, onClose, onDeleted }) {
    const open = !!employeeProp;
    // Keeps the dialog steady while it fades out after `employee` is cleared.
    const employee = useLastPresent(employeeProp);
    const [confirmText, setConfirmText] = useState('');

    const impactQuery = useEmployeeDeleteImpact(employee?.id, { enabled: open });
    const permanentlyDelete = usePermanentlyDeleteEmployee();

    // Reset between employees so a previously typed confirmation can never carry
    // over and arm the button for someone else.
    useEffect(() => {
        setConfirmText('');
    }, [employee?.id]);

    const impact = impactQuery.data;
    const confirmed = confirmText.trim().toUpperCase() === CONFIRM_WORD;
    const busy = permanentlyDelete.isPending;

    const doDelete = async () => {
        if (!employee?.id || !confirmed) return;
        try {
            const result = await permanentlyDelete.mutateAsync(employee.id);
            showToast(
                'success',
                `${employee.first_name} ${employee.last_name} was permanently deleted (${result?.totalRows || 0} related record(s) affected).`,
                { duration: 6000 }
            );
            onDeleted?.();
        } catch (error) {
            showToast('error', String(error?.message || 'Permanent delete failed.'), { duration: 8000 });
        }
    };

    return (
        <Modal open={open} onClose={busy ? undefined : onClose} onExited={() => setConfirmText('')} size="sm">
            <Modal.Header closeButton={!busy}>
                <Modal.Title>Permanently delete employee</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Message type="error" showIcon header="This cannot be undone">
                    {employee?.first_name} {employee?.last_name} ({employee?.email}) and the records below
                    will be erased from the database. There is no recovery short of a database restore.
                </Message>

                <div className="mt-3">
                    {impactQuery.isLoading ? (
                        <div className="d-flex align-items-center gap-2 text-muted">
                            <Loader size="sm" /> Checking what this would remove…
                        </div>
                    ) : impactQuery.error ? (
                        <Message type="error" showIcon>
                            {String(impactQuery.error?.message || impactQuery.error)}
                        </Message>
                    ) : (
                        <>
                            <div className="fw-semibold" style={{ fontSize: 14 }}>
                                {impact?.totalRows
                                    ? `${impact.totalRows} related record(s) affected`
                                    : 'No related records — only the employee row will be removed.'}
                            </div>

                            {impact?.deletes?.length ? (
                                <div className="mt-2">
                                    <div className="text-muted" style={{ fontSize: 13 }}>Will be deleted:</div>
                                    <ul className="mb-0" style={{ fontSize: 13 }}>
                                        {impact.deletes.map((d) => (
                                            <li key={`${d.table}`}>
                                                {d.count} × {prettyTable(d.table)}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null}

                            {impact?.clears?.length ? (
                                <div className="mt-2">
                                    <div className="text-muted" style={{ fontSize: 13 }}>
                                        Will be kept, with the reference to this employee cleared:
                                    </div>
                                    <ul className="mb-0" style={{ fontSize: 13 }}>
                                        {impact.clears.map((c) => (
                                            <li key={`${c.table}.${c.column}`}>
                                                {c.count} × {prettyTable(c.table)} <span className="text-muted">({c.column})</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>

                <div className="mt-3">
                    <label htmlFor="permanent-delete-confirm" style={{ fontSize: 13 }}>
                        Type <strong>{CONFIRM_WORD}</strong> to confirm:
                    </label>
                    <input
                        id="permanent-delete-confirm"
                        type="text"
                        className="form-control mt-1"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        autoComplete="off"
                        disabled={busy || impactQuery.isLoading || !!impactQuery.error}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onClose} disabled={busy}>
                    Cancel
                </Button>
                <Button
                    variant="danger-solid"
                    onClick={doDelete}
                    loading={busy}
                    disabled={!confirmed || impactQuery.isLoading || !!impactQuery.error}
                >
                    Delete permanently
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
